import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { AppShell } from '@components/layout/AppShell';
import { LoadingSpinner } from '@components/ui/LoadingSpinner';
import { AuthRoute } from '@auth/AuthRoute';
import { HideableRoute } from '@components/layout/HideableRoute';
import { warmDownloadToken, getApiToken } from '@lib/apiClient';
import { AuthErrorBanner } from '@components/AuthErrorBanner';
import { useIdleLogoff } from '@lib/idle-logoff';

// Lazy-load every page chunk (per spec §17 — code-split per route)
const Login = lazy(() => import('@pages/Login'));

// Setup (Phase 3 — new top-level workflow, per Claude Design "Data Portal" bundle)
const Setup = lazy(() => import('@pages/setup'));

// Dashboards (per program)
const FlightDeck = lazy(() => import('@pages/flight-deck'));
const Distribution = lazy(() => import('@pages/distribution'));
const Predictive = lazy(() => import('@pages/predictive'));
const Population = lazy(() => import('@pages/population'));
const Clinical = lazy(() => import('@pages/clinical'));
const ResultsNotification = lazy(() => import('@pages/results-notification'));

// New sections (William nav redesign 2026-05-22) — stubs, build-out later
const DocumentLibrary = lazy(() => import('@pages/document-library'));
const AdHocDispatch = lazy(() => import('@pages/ad-hoc-dispatch'));

// Section 11 · Additional Programs — top-level catalog of Stars measures
const AddOns = lazy(() => import('@pages/add-ons'));

// ClariLens — demo-only AI analytics chat. Three-layer gate for production
// tenants: the nav entry is demoMode-gated, the page itself renders a neutral
// "not enabled" card when demoMode is false (URL-walk safe), and the /api/ask
// endpoint hard-refuses on non-demo tenants.
const ClariLens = lazy(() => import('@pages/clarilens'));
// ClariLens stand-alone (dedicated give-out page): bare layout, no AppShell.
const ClariLensSolo = lazy(() => import('@pages/clarilens/Solo'));

// Section 08 · Survey & Sentiment — composite-sentiment surface (per-program)
const SurveyAndSentiment = lazy(() => import('@pages/sentiment'));

// Operations
const MemberLookup = lazy(() => import('@pages/operations/MemberLookup'));
const FileDirectory = lazy(() => import('@pages/operations/FileDirectory'));

// #33 (rev38) — Weekly Digest preview page
const WeeklyDigest = lazy(() => import('@pages/reports/WeeklyDigest'));

// Imports
const EligibilityImport = lazy(() => import('@pages/imports/Eligibility'));
const ActivityImport = lazy(() => import('@pages/imports/Activity'));
const ResponsesImport = lazy(() => import('@pages/imports/Responses'));
const FaxImport = lazy(() => import('@pages/imports/Fax'));

// Methodology + bridges
const MethodologyIndex = lazy(() => import('@pages/methodology'));
const MethodologyDetail = lazy(() => import('@pages/methodology/Detail'));
const LegacyBridge = lazy(() => import('@pages/legacy-bridge'));

// Financial
const FinancialLanding = lazy(() => import('@pages/financial'));
const InvoiceList = lazy(() => import('@pages/financial/InvoiceList'));
const InvoiceDetail = lazy(() => import('@pages/financial/InvoiceDetail'));

// Reporting Module (per Reporting_v4_Spec.md §7)
const ReportCenter = lazy(() => import('@pages/reports'));
const ReportTemplate = lazy(() => import('@pages/reports/Template'));
const ReportHistory = lazy(() => import('@pages/reports/History'));
const SchedulesConsole = lazy(() => import('@pages/reports/SchedulesConsole'));
const HistoricalLanding = lazy(() => import('@pages/reports/HistoricalLanding'));

// Admin (per spec §7 + Reporting Module §9)
const AdminUsers = lazy(() => import('@pages/admin/Users'));
const AdminAudit = lazy(() => import('@pages/admin/Audit'));
const AdminRefresh = lazy(() => import('@pages/admin/Refresh'));
const AdminActivityTypes = lazy(() => import('@pages/admin/ActivityTypes'));
const AdminEventSchemas = lazy(() => import('@pages/admin/EventSchemas'));
const AdminAuditPackets = lazy(() => import('@pages/admin/AuditPackets'));
const AdminTenants = lazy(() => import('@pages/admin/Tenants'));
const AdminExports = lazy(() => import('@pages/admin/Exports'));
const AdminFourEyes = lazy(() => import('@pages/admin/FourEyes'));
const AdminDLQ = lazy(() => import('@pages/admin/DLQ'));
const AdminIngest = lazy(() => import('@pages/admin/Ingest'));

// Print-mode pages (render-worker target routes; no AppShell)
const FlightDeckPrint = lazy(() => import('@pages/print/FlightDeckPrint'));
const ExportKitPreview = lazy(() => import('@pages/print/ExportKitPreview'));
const DistributionPrint = lazy(() => import('@pages/print/DistributionPrint'));
const PopulationPrint = lazy(() => import('@pages/print/PopulationPrint'));
const ClinicalPrint = lazy(() => import('@pages/print/ClinicalPrint'));
const CommunicationsPrint = lazy(() => import('@pages/print/CommunicationsPrint'));
const FinancialPrint = lazy(() => import('@pages/print/FinancialPrint'));
const WeeklyDigestPrint = lazy(() => import('@pages/print/WeeklyDigestPrint'));
const SentimentPrint = lazy(() => import('@pages/print/SentimentPrint'));
const PredictivePrint = lazy(() => import('@pages/print/PredictivePrint'));

// Member status (client-facing, non-PHI)
const MemberStatusSubmit = lazy(() => import('@pages/member-status'));
const MemberStatusDetail = lazy(() => import('@pages/member-status/Detail'));

// Help
const Help = lazy(() => import('@pages/Help'));

const NotFound = lazy(() => import('@pages/NotFound'));

export function App() {
  // Warm a session download token so /api asset URLs (image/audio src, download
  // anchors) can carry the ?pt= second factor synchronously at render time. No-op
  // when the bearer is disabled (flag-off) or in a print tab. Refresh under the
  // 5-minute token TTL. Also warm the API bearer once post-render (Diamond Spec
  // Rev4 D8: token warming happens AFTER render, never blocking it; each
  // apiFetch still awaits its own acquisition, this just hides first-call
  // latency on a cold load).
  useEffect(() => {
    void getApiToken();
    void warmDownloadToken();
    const id = window.setInterval(() => { void warmDownloadToken(); }, 4 * 60 * 1000);
    return () => window.clearInterval(id);
  }, []);

  // HIPAA automatic logoff: terminate the session after 60 min of inactivity.
  useIdleLogoff();

  return (
    <>
      {/* Above the Suspense boundary so a lazy-chunk load can never hide an
          active auth error (D9: the error surface must be unmissable). */}
      <AuthErrorBanner />
      <Suspense fallback={<LoadingSpinner full />}>
        <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/login/callback" element={<Login />} />
        <Route path="/logout" element={<Login />} />

        {/* Member status (client-facing, non-PHI gated) */}
        <Route path="/member-status" element={<MemberStatusSubmit />} />
        <Route path="/member-status/:requestId" element={<MemberStatusDetail />} />

        {/* Help (public) */}
        <Route path="/help" element={<Help />} />
        <Route path="/help/:topic" element={<Help />} />

        {/* Print-mode routes — render-worker targets these via Puppeteer.
            Bare layout (no AppShell). Auth still required (service account credentials). */}
        <Route element={<AuthRoute />}>
          <Route path="/print/flight-deck" element={<FlightDeckPrint />} />
          <Route path="/print/export-kit" element={<ExportKitPreview />} />
          <Route path="/print/distribution" element={<DistributionPrint />} />
          <Route path="/print/population" element={<PopulationPrint />} />
          <Route path="/print/clinical" element={<ClinicalPrint />} />
          <Route path="/print/communications" element={<CommunicationsPrint />} />
          <Route path="/print/financial" element={<FinancialPrint />} />
          <Route path="/print/sentiment" element={<SentimentPrint />} />
          <Route path="/print/predictive" element={<PredictivePrint />} />
          <Route path="/print/weekly-digest/:program" element={<WeeklyDigestPrint />} />
        </Route>

        {/* ClariLens dedicated stand-alone page: the give-out link's landing
            route (bare layout, no AppShell, no portal nav). Same AuthRoute
            gate as everything else; the demo tenant's signed-link cookie
            alone satisfies it (VITE_DEMO_BYPASS_MSAL). */}
        <Route element={<AuthRoute />}>
          <Route path="/clarilens/demo" element={<ClariLensSolo />} />
        </Route>

        {/* All other routes require auth */}
        <Route element={<AuthRoute />}>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/flight-deck" replace />} />

            {/* Setup — top-level workflow (NOT per-program; defines campaigns themselves) */}
            <Route path="/setup" element={<Setup />} />

            {/* Flight Deck — portfolio cover; canonical top-level route + post-login landing */}
            <Route path="/flight-deck" element={<FlightDeck />} />

            {/* Additional Programs — top-level catalog of Stars measures (Section 11) */}
            <Route path="/add-ons" element={<AddOns />} />

            {/* ClariLens — demo-only AI analytics chat (top-level, not per-program) */}
            <Route path="/clarilens" element={<ClariLens />} />

            {/* Per-program dashboards */}
            <Route path="/:program">
              <Route index element={<Navigate to="flight-deck" replace />} />
              <Route path="flight-deck" element={<FlightDeck />} />
              <Route path="distribution" element={<HideableRoute pageKey="distribution"><Distribution /></HideableRoute>} />
              <Route path="population" element={<HideableRoute pageKey="population"><Population /></HideableRoute>} />
              {/* 06 Interactive & Predictive — pacing/forecast, geographic, segment compare, journey, cadence */}
              <Route path="predictive" element={<HideableRoute pageKey="predictive"><Predictive /></HideableRoute>} />
              <Route path="clinical" element={<HideableRoute pageKey="clinical"><Clinical /></HideableRoute>} />
              <Route path="sentiment" element={<HideableRoute pageKey="sentiment"><SurveyAndSentiment /></HideableRoute>} />
              <Route path="results-notification" element={<ResultsNotification />} />
              <Route path="document-library" element={<HideableRoute pageKey="document-library"><DocumentLibrary /></HideableRoute>} />
              <Route path="ad-hoc-dispatch" element={<HideableRoute pageKey="ad-hoc-dispatch"><AdHocDispatch /></HideableRoute>} />

              <Route path="operations">
                <Route path="member-lookup" element={<HideableRoute pageKey="member-tracker"><MemberLookup /></HideableRoute>} />
                <Route path="file-directory" element={<FileDirectory />} />
              </Route>

              <Route path="imports">
                <Route path="eligibility" element={<EligibilityImport />} />
                <Route path="activity" element={<ActivityImport />} />
                <Route path="responses" element={<ResponsesImport />} />
                <Route path="fax" element={<FaxImport />} />
              </Route>
            </Route>

            {/* Methodology library */}
            <Route path="/methodology" element={<MethodologyIndex />} />
            <Route path="/methodology/:program/:measure" element={<MethodologyDetail />} />

            {/* Legacy KPI bridge */}
            <Route path="/legacy-bridge/:kpi" element={<LegacyBridge />} />

            {/* Financial */}
            <Route path="/financial" element={<FinancialLanding />} />
            <Route path="/financial/invoices" element={<InvoiceList />} />
            <Route path="/financial/invoices/:id" element={<InvoiceDetail />} />

            {/* Reporting Module (per Reporting_v4_Spec.md) */}
            <Route path="/reports" element={<ReportCenter />} />
            <Route path="/reports/history" element={<ReportHistory />} />
            <Route path="/reports/schedules" element={<SchedulesConsole />} />
            <Route path="/reports/weekly-digest" element={<WeeklyDigest />} />
            <Route path="/reports/view/:exportId" element={<HistoricalLanding />} />
            <Route path="/reports/:templateId" element={<ReportTemplate />} />

            {/* Admin */}
            <Route path="/admin">
              <Route path="users" element={<AdminUsers />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="refresh" element={<AdminRefresh />} />
              <Route path="activity-types" element={<AdminActivityTypes />} />
              <Route path="event-schemas" element={<AdminEventSchemas />} />
              <Route path="audit-packets" element={<AdminAuditPackets />} />
              <Route path="tenants" element={<AdminTenants />} />
              {/* Reporting Module admin */}
              <Route path="exports" element={<AdminExports />} />
              <Route path="4-eyes/:exportId" element={<AdminFourEyes />} />
              <Route path="dlq" element={<AdminDLQ />} />
              <Route path="ingest" element={<AdminIngest />} />
            </Route>
          </Route>
        </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
